import { AW_URL_QUERY_PARAM, getAwUrlParam } from '@blk/aw-utils';
import { isEmpty, isNil } from 'lodash';
import type { PkcePopupMessage } from './types';

/*
 * Inlined to avoid importing ../../Constants which pulls in heavy transitive dependencies.
 * This keeps the bootstrap module lightweight so it can run before the main app bundle loads.
 */
const MILLISECONDS_IN_SECOND = 1000;

export const PKCE_BROADCAST_CHANNEL_NAME = 'pkce-auth';
const PKCE_ORIGINAL_URL_REDIRECT_STATE_STORAGE_KEY = 'PKCE_original_url_redirect_state';
const PKCE_WORKFLOW_MODE_KEY = 'PKCE_workflow_mode';

/**
 * Compare two URLs for equality.
 * - Hash: treats empty HashRouter fragments ("", "#", "#/") as equivalent, so a
 *   URL captured at the HashRouter root matches the current location when
 *   neither is navigated to a meaningful hash route.
 * - Pathname: ignores a trailing slash (except the root "/") so "/aw-advisor/"
 *   and "/aw-advisor" are considered the same page.
 * - Origin and search string must match exactly.
 */
export const isSameUrlIgnoringEmptyHash = (a: URL, b: URL): boolean => {
  const normalizeHash = (h: string): string => (h === '' || h === '#' || h === '#/' ? '' : h);
  const normalizePathname = (p: string): string => (p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p);
  return (
    a.origin === b.origin &&
    normalizePathname(a.pathname) === normalizePathname(b.pathname) &&
    a.search === b.search &&
    normalizeHash(a.hash) === normalizeHash(b.hash)
  );
};

/**
 * Optional logging callbacks for PKCE completion bootstrap.
 * When called from the hook, these are wired to useLogger's trackEvent.
 * When called from the bootstrap script, these default to no-ops.
 */
export type PkceCompletionCallbacks = {
  onRedirectCompletion?: () => void;
  onPopupCompletion?: () => void;
  onCodeRetrievalCompletion?: () => void;
  onSneakyCompletion?: () => void;
  onError?: (errorMsg: string) => void;
  flush?: () => void;
};

/**
 * Recover the original pre-auth URL after returning from IdP redirect.
 * Appends the authorization code/state/error to the original URL and navigates via window.location.replace().
 *
 * @param storage - The Storage instance to read/write PKCE state
 * @param callbacks - Optional logging callbacks
 * @returns true if the current page is being replaced by a redirect (page lifecycle consumed), false if no redirect recovery was needed
 */
export const recoverUrlFromPkceRedirect = (storage: Storage, callbacks: PkceCompletionCallbacks = {}): boolean => {
  callbacks.onRedirectCompletion?.();
  const originalUrlState = storage.getItem(PKCE_ORIGINAL_URL_REDIRECT_STATE_STORAGE_KEY);
  if (originalUrlState) {
    // This is the redirect workflow scenario
    const updatedUrl: URL = new URL(originalUrlState);
    const error: string = getAwUrlParam(AW_URL_QUERY_PARAM.ERROR);
    if (!isEmpty(error)) {
      console.warn(`PKCE redirect authorization workflow returns error - ${error}`);
      // If error is present, redirect back to the original URL with the error
      updatedUrl.searchParams.append(AW_URL_QUERY_PARAM.ERROR, error);
      // Cleanup
      storage.removeItem(PKCE_ORIGINAL_URL_REDIRECT_STATE_STORAGE_KEY);
      // If recovered URL is identical to current location, no redirect needed — resume normal workflow
      if (isSameUrlIgnoringEmptyHash(updatedUrl, new URL(window.location.href))) {
        return false;
      }
      // Flush queued events before redirect to prevent data loss
      callbacks.flush?.();
      // Terminate routine and redirect information back to main window for error handling
      window.location.replace(updatedUrl.toString());
      return true;
    }
    const pkceCode: string = getAwUrlParam(AW_URL_QUERY_PARAM.CODE);
    if (isEmpty(pkceCode)) {
      console.warn('PKCE original URL state exist, but PKCE code is missing in the URL.');
    } else {
      updatedUrl.searchParams.append(AW_URL_QUERY_PARAM.CODE, pkceCode);
    }
    const state: string = getAwUrlParam(AW_URL_QUERY_PARAM.STATE);
    if (!isEmpty(state)) {
      updatedUrl.searchParams.append(AW_URL_QUERY_PARAM.STATE, state);
    }
    storage.removeItem(PKCE_ORIGINAL_URL_REDIRECT_STATE_STORAGE_KEY);
    // If recovered URL is identical to current location, no redirect needed — resume normal workflow
    if (isSameUrlIgnoringEmptyHash(updatedUrl, new URL(window.location.href))) {
      return false;
    }
    // Flush queued events before redirect to prevent data loss
    callbacks.flush?.();
    // Recover the original URL state before initializing advisor config and PKCE workflow, after receiving the access_token recover back to the original URL
    window.location.replace(updatedUrl.toString());
    return true;
  }
  return false;
};

/**
 * Handle PKCE completion in the popup window context.
 * Broadcasts the authorization code/state/error via BroadcastChannel and optionally closes the popup.
 *
 * @param isClosable - whether the popup window should be closed after posting the message
 * @param callbacks - Optional logging callbacks
 * @returns true if the current page is a popup/iframe consumed by PKCE completion handling, false if not in a popup context
 */
export const handlePkcePopupCompletion = (isClosable: boolean, callbacks: PkceCompletionCallbacks = {}): boolean => {
  callbacks.onPopupCompletion?.();
  // Preemptively terminate the workflow if we know that it's currently in iframe for popup workflow
  if (window !== window.parent) {
    const errorMsg =
      'Terminating PKCE popup workflow as current page is an iframe, popup mode is not supported for iframe.';
    console.error(errorMsg);
    callbacks.onError?.(errorMsg);
    return true;
  }

  /*
   * DO NOT MOVE THIS CHECK TO THE TOP, THERE IS A KNOWN ISSUE FOR CLIENTS WITH IFRAME SETUP THAT MAY CAUSE APP TO BREAK
   * Reference: https://dev.azure.com/1A4D/Aladdin%20Wealth/_workitems/edit/1656721
   */
  if (!isNil(window.opener)) {
    // This is the popup window scenario, we need to call the callback function in the parent window and close the popup window to continue the PKCE workflow
    const pkceError: string | undefined = getAwUrlParam(AW_URL_QUERY_PARAM.ERROR);
    if (!isEmpty(pkceError)) {
      console.error(`PKCE error: ${pkceError}`);
    }
    const pkceCode: string = getAwUrlParam(AW_URL_QUERY_PARAM.CODE);
    const state: string = getAwUrlParam(AW_URL_QUERY_PARAM.STATE);

    // Only broadcast if there is meaningful data (code or error) to send
    if (isEmpty(pkceCode) && isEmpty(pkceError)) {
      return false;
    }

    const message: PkcePopupMessage = { pkceCode, state, error: pkceError, type: 'popup' };

    try {
      const channel = new BroadcastChannel(PKCE_BROADCAST_CHANNEL_NAME);
      channel.postMessage(message);
      channel.close();
    } catch (e) {
      const errorMsg = 'Failed to broadcast PKCE popup message';
      console.error(`${errorMsg}: ${e}`);
      callbacks.onError?.(errorMsg);
    }

    setTimeout(() => {
      isClosable && window.close();
    }, MILLISECONDS_IN_SECOND);
    return true;
  }
  return false;
};

/**
 * This function will perform early termination before app initialisation if conditions are met.
 * We should perform early termination because there's no reason to load the whole app init again after IDP authentication.
 * Since we just need to "capture" the "code" or "error" and reinitialise the app (redirect) or propagate them back to main window (popup)
 *
 * @param storage - The Storage instance to read/write PKCE state
 * @param callbacks - Optional logging callbacks
 * @returns true, when current page lifecycle has been consumed by PKCE completion handling and app init should stop.
 */
export const handlePkceAuthorizationCompletionBootstrap = (
  storage: Storage,
  callbacks: PkceCompletionCallbacks = {}
): boolean => {
  const workflowMode: string | null = storage.getItem(PKCE_WORKFLOW_MODE_KEY);
  if (!isNil(workflowMode)) {
    callbacks.onCodeRetrievalCompletion?.();
    /*
     * This try catch is crutial to prevent unexpected issues from crashing the app.
     * PKCE should not stop user from accessing the app (unless it's redirect workflow)
     */
    try {
      if (workflowMode === 'redirect') {
        storage.removeItem(PKCE_WORKFLOW_MODE_KEY);
        return recoverUrlFromPkceRedirect(storage, callbacks);
      }
      if (workflowMode === 'popup') {
        // Here is just simply removing from popup window's session
        storage.removeItem(PKCE_WORKFLOW_MODE_KEY);
        return handlePkcePopupCompletion(true, callbacks);
      }
      /* istanbul ignore next */
      console.error(`Unrecognised or unhandled PKCE workflow mode - ${workflowMode}`);
      storage.removeItem(PKCE_WORKFLOW_MODE_KEY);
      return false;
    } catch (e) {
      /* istanbul ignore next */
      const errorMsg = 'PKCE authorization completion workflow failure';
      console.error(`${errorMsg} - ${e}`);
      callbacks.onError?.(errorMsg);
      storage.removeItem(PKCE_WORKFLOW_MODE_KEY);
      return false;
    }
  }

  const pkceCode: string = getAwUrlParam(AW_URL_QUERY_PARAM.CODE);
  if (!isEmpty(pkceCode)) {
    callbacks.onSneakyCompletion?.();
    /*
     * Attempt to broadcast the code via BroadcastChannel even without workflow mode in storage.
     * This covers scenarios where session/storage was lost but the code is still present in the URL.
     */
    try {
      console.warn('Attempting to sneakily broadcast PKCE code because "code" exist in the query param.');
      /*
       * Important: Fire-and-forget — do NOT return the result of handlePkcePopupCompletion.
       * If evaluated and returned true, it would terminate app initialization early (AdvisorContainer skips initializeAppSaga),
       * causing the app to hang when opened via window.open() with a redirect-based PKCE flow.
       */
      handlePkcePopupCompletion(false, callbacks);
    } catch (e) {
      /* istanbul ignore next */
      console.warn(`Failed to sneakily broadcast the PKCE "code" - ${e}`);
    }
  }

  return false;
};
